import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AttendeeDetailModal from '../../components/AttendeeDetailModal/AttendeeDetailModal';
import './AttendeesPage.css';

// Import magic circle
import MagicCircle from '../../img/icons/magic_circle.png';

const AttendeesPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [attendeesData, setAttendeesData] = useState(null);
  const [selectedAttendee, setSelectedAttendee] = useState(null);
  const [showKudosInfo, setShowKudosInfo] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';
  const getToken = () => localStorage.getItem('token');

  // Fetch attendees data
  const fetchAttendees = useCallback(async () => {
    const token = getToken();
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);

      const response = await fetch(
        `${API_BASE}/user-kudos/event/${eventId}/attendees?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const data = await response.json();

      if (data.success) {
        setAttendeesData(data);
      } else if (!data.canViewAttendees) {
        navigate(`/event/${eventId}`);
      } else {
        console.error('Failed to fetch attendees:', data.error);
      }
    } catch (error) {
      console.error('Error fetching attendees:', error);
    } finally {
      setLoading(false);
    }
  }, [eventId, searchTerm, API_BASE, navigate]);

  useEffect(() => {
    fetchAttendees();
  }, [fetchAttendees]);

  // Open detail modal for an attendee
  const handleAttendeeClick = (attendee) => {
    setSelectedAttendee(attendee);
  };

  // Handle kudos given from modal
  const handleKudosGiven = (attendeeId, kudosType, newData) => {
    setAttendeesData(prev => ({
      ...prev,
      kudosGivenCount: newData.kudosGivenCount,
      kudosRemaining: newData.kudosRemaining,
      attendees: prev.attendees.map(a =>
        a._id === attendeeId
          ? { ...a, kudosGiven: [...(a.kudosGiven || []), kudosType] }
          : a
      )
    }));
    // Update selected attendee too
    setSelectedAttendee(prev => prev ? {
      ...prev,
      kudosGiven: [...(prev.kudosGiven || []), kudosType]
    } : null);
  };

  // Search handler with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== '') {
        fetchAttendees();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  if (loading) {
    return (
      <div className="attendees-page">
        <div className="attendees-container loading">
          <div className="spinner"></div>
          <p>Loading attendees...</p>
        </div>
      </div>
    );
  }

  if (!attendeesData?.canViewAttendees) {
    return (
      <div className="attendees-page">
        <div className="attendees-container access-denied">
          <i className="bi bi-lock"></i>
          <h2>Attendee List Locked</h2>
          <p>{attendeesData?.message || 'The attendee list is not yet available.'}</p>
          <Link to={`/event/${eventId}`} className="btn-back">
            <i className="bi bi-arrow-left"></i> Back to Event
          </Link>
        </div>
      </div>
    );
  }

  const { attendees, confirmedCount, kudosRemaining, eventTitle } = attendeesData;

  return (
    <div className="attendees-page">

      <div className="attendees-container">
        {/* Header */}
        <div className="attendees-header">
          <Link to={`/event/${eventId}`} className="back-link">
            <i className="bi bi-arrow-left"></i>
          </Link>
          <div className="header-content">
            <h1>Give Kudos</h1>
            <p className="event-title">{eventTitle}</p>
          </div>
        </div>

        {/* Main layout: Grid left, Panel right */}
        <div className="attendees-layout">
          {/* Left: Attendees Grid */}
          <div className="attendees-main">
            <div className="attendees-grid">
              {attendees.length === 0 ? (
                <div className="no-results">
                  <i className="bi bi-search"></i>
                  <p>No attendees found matching "{searchTerm}"</p>
                </div>
              ) : (
                attendees.map((attendee) => {
                  const hasGivenKudos = attendee.kudosGiven && attendee.kudosGiven.length > 0;

                  return (
                    <div
                      key={attendee._id}
                      className={`attendee-tile ${hasGivenKudos ? 'has-kudos' : ''}`}
                      onClick={() => handleAttendeeClick(attendee)}
                    >
                      <div className="tile-avatar-wrapper">
                        <img src={MagicCircle} alt="" className="tile-magic-circle" />
                        <div className="tile-avatar">
                          {attendee.profilePicture ? (
                            <img src={attendee.profilePicture} alt={attendee.fullName} />
                          ) : (
                            <span className="tile-initial">
                              {attendee.fullName?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          )}
                        </div>
                        {hasGivenKudos && (
                          <div className="kudos-count-badge">{attendee.kudosGiven.length}</div>
                        )}
                      </div>
                      <span className="tile-name">{attendee.fullName?.split(' ')[0]}</span>
                    </div>
                  );
                })
              )}
            </div>

            {/* Info footer */}
            <div className="attendees-footer">
              <p className="privacy-note">
                <i className="bi bi-shield-check"></i>
                Kudos are anonymous - recipients won't know who gave them.
              </p>
            </div>
          </div>

          {/* Right: Stats & Search Panel */}
          <div className="attendees-panel">
            {/* Stats */}
            <div className="panel-stats">
              <div className="panel-stat">
                <i className="bi bi-people-fill"></i>
                <div className="panel-stat-info">
                  <span className="panel-stat-value">{confirmedCount}</span>
                  <span className="panel-stat-label">Attendees</span>
                </div>
              </div>
              <div className="panel-stat highlight" style={{ position: 'relative' }}>
                <i className="bi bi-star-fill"></i>
                <div className="panel-stat-info">
                  <span className="panel-stat-value">{kudosRemaining}</span>
                  <span className="panel-stat-label">Kudos Left</span>
                </div>
                <span
                  className="kudos-info-icon"
                  onMouseEnter={() => setShowKudosInfo(true)}
                  onMouseLeave={() => setShowKudosInfo(false)}
                >
                  <i className="bi bi-info-circle"></i>
                  {showKudosInfo && (
                    <div className="kudos-info-tooltip">
                      You can give Kudos to up to 5 special people per event!
                    </div>
                  )}
                </span>
              </div>
            </div>

            {/* Search */}
            <div className="panel-search">
              <div className="search-container">
                <i className="bi bi-search"></i>
                <input
                  type="text"
                  placeholder="Search attendees..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                {searchTerm && (
                  <button
                    className="clear-search"
                    onClick={() => setSearchTerm('')}
                  >
                    <i className="bi bi-x"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Kudos Legend */}
            <div className="panel-legend">
              <h4>Kudos Types</h4>
              <div className="legend-list">
                <div className="legend-item">
                  <span className="legend-dot personality"></span>
                  <span>Personality & Presence</span>
                </div>
                <div className="legend-item">
                  <span className="legend-dot technique"></span>
                  <span>Excellent Technique</span>
                </div>
                <div className="legend-item">
                  <span className="legend-dot creativity"></span>
                  <span>Creativity & Play</span>
                </div>
                <div className="legend-item">
                  <span className="legend-dot connection"></span>
                  <span>Safe Space & Connection</span>
                </div>
                <div className="legend-item">
                  <span className="legend-dot magic"></span>
                  <span>Other Magic</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Attendee Detail Modal */}
      {selectedAttendee && (
        <AttendeeDetailModal
          isOpen={!!selectedAttendee}
          onClose={() => setSelectedAttendee(null)}
          attendee={selectedAttendee}
          eventId={eventId}
          kudosRemaining={kudosRemaining}
          onKudosGiven={handleKudosGiven}
        />
      )}
    </div>
  );
};

export default AttendeesPage;
